/**
 * The single place in the frontend that reads `import.meta.env`.
 *
 * None of these values are secrets — a Stripe publishable key, a Cognito pool
 * id, and two API Gateway URLs are all public by design, and they ship in the
 * bundle either way. They live here rather than inline for a different reason:
 * a development build must be able to point at Stripe test mode, and that is
 * impossible when the live key is a literal in a component.
 *
 * Values are supplied by Vite's env files:
 *   .env               committed, shared defaults (production endpoints)
 *   .env.development   committed, overrides for `npm start`
 *   .env.production    committed, loaded by `npm run build` — the live Stripe
 *                      publishable key and VITE_FUNDRAISING_API_BASE_URL
 *   .env.local         gitignored, personal overrides — put test keys here
 */

// `vite/client` types ImportMetaEnv with an `any` index signature. Narrowing it
// once here keeps every read below properly typed as `string | undefined`.
const env = import.meta.env as unknown as Record<string, string | undefined>;

/**
 * Reads an optional variable, treating empty as unset.
 *
 * `.env.development` sets keys to the empty string precisely to mean "not
 * configured", so `??` would be wrong here — it only replaces null/undefined.
 */
const optional = (value: string | undefined): string | undefined => {
    if (!value) {
        return undefined;
    }
    return value;
};

const required = (name: string, value: string | undefined): string => {
    if (!value) {
        throw new Error(
            `${name} is not set. Copy .env.example entries into .env.local, or check that ` +
                'the build environment defines it.',
        );
    }
    return value;
};

export const COGNITO_REGION = required('VITE_COGNITO_REGION', env.VITE_COGNITO_REGION);

export const COGNITO_USER_POOL_ID = required(
    'VITE_COGNITO_USER_POOL_ID',
    env.VITE_COGNITO_USER_POOL_ID,
);

export const COGNITO_CLIENT_ID = required('VITE_COGNITO_CLIENT_ID', env.VITE_COGNITO_CLIENT_ID);

export const CHARITY_API_BASE_URL = required(
    'VITE_CHARITY_API_BASE_URL',
    env.VITE_CHARITY_API_BASE_URL,
);

export const USERS_API_BASE_URL = required('VITE_USERS_API_BASE_URL', env.VITE_USERS_API_BASE_URL);

/**
 * When set, event reads go to the new `fundraising` service instead of the
 * legacy API (docs/migration-plan.md, Phase 2). Unsetting it is the rollback:
 * one variable, no code deploy.
 */
export const FUNDRAISING_API_BASE_URL: string | undefined = optional(
    env.VITE_FUNDRAISING_API_BASE_URL,
);

/**
 * Deliberately optional, and deliberately empty in `.env.development`.
 *
 * An unset key disables the checkout form rather than failing the build, so a
 * developer can run the site locally without credentials. What it must never do
 * is silently fall back to the live key — that would put real cards behind a
 * `npm start`.
 */
export const STRIPE_PUBLISHABLE_KEY: string | undefined = optional(env.VITE_STRIPE_PUBLISHABLE_KEY);

/**
 * Base URL of the CloudFront distribution serving event photography
 * (`cdk/assets_stack.py`, output `AssetsBaseUrl`).
 *
 * Optional, and its absence is a working state rather than an error: galleries
 * render nothing, exactly as they already do for an event with no photos. That
 * matters because the bucket has to exist and be synced before this is set, and
 * a required variable would turn that ordering into a broken build.
 */
export const ASSETS_BASE_URL: string | undefined = optional(env.VITE_ASSETS_BASE_URL);
